import Icon from '@/components/ui/AppIcon';

interface SafetyMetric {
  id: number;
  label: string;
  value: string;
  icon: string;
  description: string;
}

interface SafetyStandard {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface SafetyStandardsProps {
  metrics: SafetyMetric[];
  standards: SafetyStandard[];
}

export default function SafetyStandards({ metrics, standards }: SafetyStandardsProps) {
  return (
    <section className="bg-card py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-primary mb-4">
            Safety Standards & Records
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto">
            Unwavering commitment to safety excellence with industry-leading standards and zero-compromise protocols
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {metrics.map((metric) => (
            <div
              key={metric.id}
              className="bg-surface rounded-lg p-6 text-center shadow-subtle hover:shadow-modal transition-shadow duration-300"
            >
              <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon
                  name={metric.icon as any}
                  size={32}
                  className="text-success-foreground"
                />
              </div>
              <div className="text-3xl font-headline font-bold text-primary mb-2">
                {metric.value}
              </div>
              <div className="text-sm font-body font-semibold text-textPrimary mb-2">
                {metric.label}
              </div>
              <p className="text-xs text-textSecondary">{metric.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-background rounded-lg p-8">
          <h3 className="text-2xl font-headline font-bold text-textPrimary mb-6 text-center">
            Our Safety Protocols
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {standards.map((standard) => (
              <div
                key={standard.id}
                className="flex items-start bg-card rounded-lg p-6 shadow-subtle"
              >
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Icon
                    name={standard.icon as any}
                    size={24}
                    className="text-primary-foreground"
                  />
                </div>
                <div>
                  <h4 className="text-lg font-headline font-bold text-textPrimary mb-2">
                    {standard.title}
                  </h4>
                  <p className="text-sm text-textSecondary leading-relaxed">
                    {standard.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}